import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Box, Text, VStack, Heading, Image, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { TEXTS } from "../constants/texts";
// import Dummy from "../images/Dummy.png";
import WowowLogo from "../images/WowowLogo.png";

const preventDefault = (e: React.MouseEvent): void => {
  e.preventDefault();
};

const colors = {
  brown: "rgb(60, 17, 0)",
  beige: "rgb(241, 236, 222)",
  gold: "rgb(207, 189, 143)",
};

const Cautions: React.FC = () => {
  // スクロール方向を保持する状態
  const [scrollDirection, setScrollDirection] = useState<"down" | "up">("down");
  const prevScrollY = useRef(0);

  // 初期ページロード状態の追跡
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 各要素のアニメーション状態を追跡
  const [titleAnimationState, setTitleAnimationState] = useState("hidden");
  const [cautionsTextAnimationState, setCautionsTextAnimationState] =
    useState("hidden");
  const [creditsTextAnimationState, setCreditsTextAnimationState] =
    useState("hidden");

  // コンポーネントの位置情報を保持
  const cautionsRef = useRef<HTMLDivElement>(null);

  // useInView設定の最適化
  const useInViewConfig = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.3,
    }),
    []
  );

  // コンポーネント全体がビューポートに入ったかどうかを検出
  const { ref: componentRef } = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  // useInViewフック
  const { ref: titleRef, inView: titleInView } = useInView(useInViewConfig);
  const { ref: cautionsTextRef, inView: cautionsTextInView } =
    useInView(useInViewConfig);
  const { ref: creditsTextRef, inView: creditsTextInView } =
    useInView(useInViewConfig);

  // 初期ロード時の表示要素を検出
  useEffect(() => {
    if (isInitialLoad) {
      const handleInitialVisibility = () => {
        const scrollY = window.scrollY;
        const viewportHeight = window.innerHeight;
        const viewportBottom = scrollY + viewportHeight;

        if (!cautionsRef.current) return;

        const cautionsRect = cautionsRef.current.getBoundingClientRect();
        const cautionsTop = cautionsRect.top + scrollY;
        const cautionsHeight = cautionsRect.height;

        const isVisible =
          cautionsTop < viewportBottom &&
          cautionsTop + cautionsHeight > scrollY;

        if (isVisible) {
          setTitleAnimationState("visible");
          setCautionsTextAnimationState("visible");
          setCreditsTextAnimationState("visible");
        }

        setIsInitialLoad(false);
      };

      if (document.readyState === "complete") {
        handleInitialVisibility();
      } else {
        window.addEventListener("load", handleInitialVisibility, {
          once: true,
        });
        return () =>
          window.removeEventListener("load", handleInitialVisibility);
      }
    }
  }, [isInitialLoad]);

  // スクロール方向検出と要素のアニメーション状態の更新
  useEffect(() => {
    prevScrollY.current = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > prevScrollY.current;

      setScrollDirection(isScrollingDown ? "down" : "up");
      prevScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // タイトルアニメーション状態の更新
  useEffect(() => {
    if (titleInView) {
      // 即時実行のrequestAnimationFrameでレンダリングを最適化
      requestAnimationFrame(() => {
        if (scrollDirection === "up") {
          setTitleAnimationState("instant");
        } else {
          // 下スクロール時は常にvisibleにリセットしてからアニメーション
          setTitleAnimationState("visible");
        }
      });
    } else if (scrollDirection === "up") {
      // ビューポートから外れたときの処理（上スクロール時のみ）
      setTitleAnimationState("hidden");
    }
  }, [titleInView, scrollDirection]);

  // コンテンツアニメーション状態の更新
  useEffect(() => {
    if (cautionsTextInView) {
      // 即時実行のrequestAnimationFrameでレンダリングを最適化
      requestAnimationFrame(() => {
        if (scrollDirection === "up") {
          setCautionsTextAnimationState("instant");
        } else {
          setCautionsTextAnimationState("visible");
        }
      });
    } else if (scrollDirection === "up") {
      // ビューポートから外れたときの処理（上スクロール時のみ）
      setCautionsTextAnimationState("hidden");
    }
  }, [cautionsTextInView, scrollDirection]);

  // クレジットアニメーション状態の更新
  useEffect(() => {
    if (creditsTextInView) {
      // 即時実行のrequestAnimationFrameでレンダリングを最適化
      requestAnimationFrame(() => {
        if (scrollDirection === "up") {
          setCreditsTextAnimationState("instant");
        } else {
          setCreditsTextAnimationState("visible");
        }
      });
    } else if (scrollDirection === "up") {
      // ビューポートから外れたときの処理（上スクロール時のみ）
      setCreditsTextAnimationState("hidden");
    }
  }, [creditsTextInView, scrollDirection]);

  // 共通アニメーションのvariants
  const fadeInVariants = useMemo(
    () => ({
      hidden: {
        opacity: 0,
        y: 20,
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 1.2,
          ease: "easeOut",
        },
      },
      instant: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0,
        },
      },
    }),
    []
  );

  // 遅延付きアニメーションのvariants生成関数
  const getDelayedVariants = useCallback(
    (index: number) => ({
      hidden: {
        opacity: 0,
        y: 20,
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 1.2,
          delay: index * 0.1,
          ease: "easeOut",
        },
      },
      instant: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0,
        },
      },
    }),
    []
  );

  // 要素を参照で結合する関数
  const combineRefs = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        cautionsRef.current = element;
        if (typeof componentRef === "function") {
          componentRef(element);
        }
      }
    },
    [componentRef]
  );

  return (
    <Box bg="white" position="relative" width="100%" ref={combineRefs}>
      {/* 上部のベージュ背景エリア */}
      <Box
        bg={colors.beige}
        py={{
          base: "30px",
          sm: "20px",
          md: "50px",
          lg: "60px",
        }}
        pos="relative"
        width="100%"
      >
        {/* タイトル部分 */}
        <Box
          ref={titleRef}
          w="100%"
          position="relative"
          mb={{
            base: "50px",
            sm: "30px",
            md: "70px",
            lg: "80px",
          }}
        >
          <motion.div
            initial="hidden"
            animate={titleAnimationState}
            variants={fadeInVariants}
          >
            <Heading
              textAlign="center"
              w={{
                base: "90%",
                md: "80%",
              }}
              maxW="1140px"
              display="block"
              mx="auto"
              px={{
                base: 4,
                sm: 6,
                md: 0,
                lg: 0,
              }}
              py={{
                base: 1,
                sm: 1.5,
                md: 2,
              }}
              fontSize={{
                base: "13px",
                sm: "15px",
                md: "20px",
                lg: "30px",
                xl: "32px",
                "2xl": "36px",
              }}
              bg="transparent"
              zIndex={2}
              color={colors.brown}
              sx={{
                position: "relative",
                width: "100%",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: {
                    base: "calc(8% + 4px)",
                    sm: "calc(8% + 6px)",
                    md: 0,
                    lg: 0,
                  },
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: {
                    base: "30%",
                    sm: "30%",
                    md: "35%",
                    lg: "35%",
                  },
                  height: {
                    base: "2px",
                    sm: "2px",
                    md: "2px",
                    lg: "3px",
                    xl: "3px",
                    "2xl": "3px",
                  },
                  background: colors.gold,
                  opacity:
                    titleInView || titleAnimationState !== "hidden" ? 1 : 0,
                  transition: "opacity 1.2s ease-out",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  right: {
                    base: "calc(8% + 4px)",
                    sm: "calc(8% + 6px)",
                    md: 0,
                    lg: 0,
                  },
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: {
                    base: "30%",
                    sm: "30%",
                    md: "35%",
                    lg: "35%",
                  },
                  height: {
                    base: "2px",
                    sm: "2px",
                    md: "2px",
                    lg: "3px",
                    xl: "3px",
                    "2xl": "3px",
                  },
                  background: colors.gold,
                  opacity:
                    titleInView || titleAnimationState !== "hidden" ? 1 : 0,
                  transition: "opacity 1.2s ease-out",
                },
              }}
            >
              {TEXTS.Cautions.content[0]}
            </Heading>
          </motion.div>
        </Box>

        {/* テキストコンテンツ */}
        <Box
          w={{
            base: "90%",
            md: "80%",
          }}
          maxW="1140px"
          mx="auto"
          mt={{
            base: -12,
            sm: -8,
            md: "calc(-4rem)",
            lg: -20,
            xl: -20,
            "2xl": -20,
          }}
          px={{
            base: 4,
            sm: 6,
            md: 0,
            lg: 0,
          }}
        >
          <VStack
            spacing={3}
            align={{
              base: "center",
              md: "flex-start",
            }}
          >
            <Box ref={cautionsTextRef} width="100%">
              <motion.div
                initial="hidden"
                animate={cautionsTextAnimationState}
                variants={fadeInVariants}
              >
                {TEXTS.Cautions.content.slice(1, 10).map((text, index) => (
                  <motion.div
                    key={index}
                    initial="hidden"
                    animate={cautionsTextAnimationState}
                    variants={getDelayedVariants(index)}
                  >
                    <Text
                      color={colors.brown}
                      fontSize={{
                        base: "11px",
                        sm: "12px",
                        md: "14px",
                        lg: "16px",
                        xl: "20px",
                        "2xl": "20px",
                      }}
                      fontWeight="bold"
                      lineHeight={{
                        base: "1.5",
                        sm: "1.5",
                        md: "1.5",
                        lg: "1.3",
                      }}
                      letterSpacing={{
                        base: "0.02em",
                        sm: "0.02em",
                        md: "normal",
                      }}
                      overflowWrap="break-word"
                      whiteSpace={{
                        base: "normal",
                        md: "normal",
                        lg: "nowrap",
                      }}
                      textAlign="left"
                    >
                      {text}
                    </Text>
                  </motion.div>
                ))}
                <Box
                  w="100%"
                  maxW="1140px"
                  h={{
                    base: "2px",
                    sm: "2px",
                    md: "2px",
                    lg: "3px",
                    xl: "3px",
                    "2xl": "3px",
                  }}
                  bg={colors.gold}
                  mt={{
                    base: 4,
                    sm: 5,
                    md: 6,
                    lg: 8,
                  }}
                  mx="auto"
                  sx={{
                    opacity:
                      cautionsTextInView ||
                      cautionsTextAnimationState !== "hidden"
                        ? 1
                        : 0,
                    transition: "opacity 1.2s ease-out 1.2s",
                  }}
                />
              </motion.div>
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* 下部の白背景エリア */}
      <Box
        bg="white"
        w="100%"
        h={{
          base: "90px",
          sm: "60px",
          md: "100px",
          lg: "130px",
          xl: "200px",
          "2xl": "200px",
        }}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {/* クレジットテキスト */}
        <Box
          ref={creditsTextRef}
          w="100%"
          px={{
            base: 4,
            sm: 6,
            md: 8,
            lg: 10,
          }}
        >
          <motion.div
            initial="hidden"
            animate={creditsTextAnimationState}
            variants={fadeInVariants}
          >
            <Text
              color={colors.brown}
              fontSize={{
                base: "9px",
                sm: "9px",
                md: "14px",
                lg: "20px",
                xl: "22px",
                "2xl": "24px",
              }}
              fontWeight="bold"
              textAlign="center"
              w="100%"
              whiteSpace={{ base: "pre-line", sm: "normal" }}
            >
              {[0, 1].map((index) => (
                <>
                  <motion.div
                    key={index}
                    initial="hidden"
                    animate={creditsTextAnimationState}
                    variants={getDelayedVariants(index)}
                  >
                    {TEXTS.Credits.content[index]}
                  </motion.div>
                  {index < 1 && <Box height="0.5em" />}
                </>
              ))}
            </Text>
          </motion.div>
        </Box>
      </Box>

      {/* フッターエリア（茶色部分） - 位置を固定 */}
      <Box
        w="100%"
        h={{
          base: "50px",
          sm: "50px",
          md: "80px",
          lg: "100px",
          xl: "120px",
          "2xl": "120px",
        }}
        bg={colors.brown}
        position="relative"
        bottom="0"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack
          spacing={{
            base: 1,
            sm: 1,
            md: 2,
            lg: 3,
          }}
          align="center"
        >
          <Text
            color="white"
            fontSize={{
              base: "10px",
              sm: "11px",
              md: "14px",
              lg: "18px",
              xl: "20px",
              "2xl": "20px",
            }}
            fontWeight="bold"
            textAlign="center"
          >
            {TEXTS.Credits.content[2]}
          </Text>
          <Flex
            align="center"
            gap={{
              base: 2,
              sm: 2,
              md: 4,
              lg: 4,
            }}
          >
            <Text
              color="white"
              fontSize={{
                base: "10px",
                sm: "11px",
                md: "14px",
                lg: "18px",
                xl: "20px",
                "2xl": "20px",
              }}
              fontWeight="bold"
            >
              {TEXTS.Credits.content[3]}
            </Text>
            <Image
              src={WowowLogo}
              alt="wowow"
              h={{
                base: "12px",
                sm: "12px",
                md: "18px",
                lg: "20px",
                xl: "22px",
                "2xl": "24px",
              }}
              w="auto"
              onContextMenu={preventDefault}
              draggable={false}
            />
          </Flex>
        </VStack>
      </Box>
    </Box>
  );
};

export default Cautions;
